import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { isSystemId } from '../../utils/systemId';
import { CreditCard, AlertTriangle, CheckCircle2, Building2, History, Loader2 } from 'lucide-react';
import { getPaymentHolds, setPaymentHold } from '../../redux/paymentSlice';
import { getTenantOptions } from '../../redux/tenantSlice';

// WHO HELD IT (owner ruling, 29 Sep 2026: no system id on screen). The server
// now sends `heldByName` — the person's name, else the e-mail `heldBy` already
// carried, else null — and that is what is drawn. An older server sends only
// `heldBy` (an e-mail, or a user id when the account had none): the e-mail is
// drawn and anything id-shaped (a business key or a bare numeric PK) is not.
const holdActor = (h) => {
    if (!h) return null;
    if (Object.prototype.hasOwnProperty.call(h, 'heldByName')) return h.heldByName || null;
    const by = h.heldBy ? String(h.heldBy) : '';
    if (!by || isSystemId(by) || /^\d+$/.test(by)) return null;
    return by;
};

// Super Admin's payment screen is MAINTENANCE, not configuration.
//
// Whether a hospital accepts cash is that hospital's business choice and lives
// in their own Settings. Whether a method is WORKING is a platform problem, and
// that is this screen: put a method on hold during an outage, release it when
// fixed.
//
// A hold is a temporary COVER — it never writes to a hospital's config, so
// releasing it restores every hospital to exactly what it had. Without that,
// recovering from a one-hour gateway outage would mean contacting every
// hospital to ask what they used to have switched on.
export default function PlatformPaymentsTab() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const holds = useSelector((s) => s.payment?.holds);
    const loading = useSelector((s) => s.payment?.holdsLoading);
    const tenantOptions = useSelector((s) => s.tenant?.tenantOptions);

    // Per-method draft state for the reason box and the scope picker.
    const [draft, setDraft] = useState({});
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        dispatch(getPaymentHolds());
        dispatch(getTenantOptions());
    }, [dispatch]);

    const setField = (key, field, value) =>
        setDraft((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));

    const apply = async (method, held) => {
        const d = draft[method] || {};
        // '' in the scope picker means every hospital.
        const tenantId = d.scope ? d.scope : null;
        setBusy(method);
        try {
            await dispatch(setPaymentHold({ method, tenantId, held, reason: d.reason || '' })).unwrap();
            toast.success(
                held
                    ? t('payments.hold_placed', 'Payment method placed on hold')
                    : t('payments.hold_released', 'Hold released — hospitals restored')
            );
        } catch (err) {
            toast.error(typeof err === 'string' ? err : t('payments.hold_failed', 'Could not update the hold'));
        } finally {
            setBusy(null);
        }
    };

    const methods = holds?.methods || [];

    return (
        <div data-testid="platform-payments-tab" className="space-y-6">
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                    {t('payments.maintenance_intro',
                        'This is maintenance control, not configuration. Each hospital chooses which methods it accepts in its own Settings. Putting a method on hold here temporarily blocks it — their choice is remembered and comes back automatically when you release the hold.')}
                </span>
            </div>

            {loading && !holds ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.loading', 'Loading...')}
                </div>
            ) : (
                <div className="rounded-2xl border border-gray-200">
                    <div className="flex items-center gap-2 p-4 border-b border-gray-100">
                        <CreditCard className="w-4 h-4 text-[#4ABDBD]" />
                        <h3 className="font-semibold text-gray-800">
                            {t('payments.availability', 'Payment method availability')}
                        </h3>
                    </div>

                    <div className="divide-y divide-gray-100">
                        {methods.map((m) => {
                            const d = draft[m.key] || {};
                            const isBusy = busy === m.key;
                            return (
                                <div key={m.key} data-testid={`platform-payment-${m.key}`} className="p-4">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-medium text-gray-800 text-sm flex items-center gap-2 flex-wrap">
                                                {m.label}
                                                {m.held ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                                                        <AlertTriangle className="w-3 h-3" />
                                                        {t('payments.on_hold', 'ON HOLD')}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                                        <CheckCircle2 className="w-3 h-3" />
                                                        {t('payments.available', 'Available')}
                                                    </span>
                                                )}
                                            </div>

                                            {m.globalHold && (
                                                <p className="text-xs text-red-700 mt-1.5">
                                                    {t('payments.held_all', 'Held for ALL hospitals')}
                                                    {m.globalHold.reason ? ` — ${m.globalHold.reason}` : ''}
                                                    <span className="text-gray-400"> · {m.affectedTenants} {t('payments.hospitals', 'hospitals')}</span>
                                                </p>
                                            )}

                                            {(m.tenantHolds || []).map((h) => (
                                                <p key={h.tenantId} className="text-xs text-amber-800 mt-1 flex items-center gap-1.5">
                                                    <Building2 className="w-3 h-3" />
                                                    {(tenantOptions || []).find((x) => x.tenantId === h.tenantId)?.name || t('healthRecord.details.hospital', 'Hospital')}
                                                    {h.reason ? ` — ${h.reason}` : ''}
                                                    <button
                                                        type="button"
                                                        data-testid={`release-tenant-hold-${m.key}-${h.tenantId}`}
                                                        onClick={async () => {
                                                            setBusy(m.key);
                                                            try {
                                                                await dispatch(setPaymentHold({ method: m.key, tenantId: h.tenantId, held: false })).unwrap();
                                                                toast.success(t('payments.hold_released', 'Hold released — hospitals restored'));
                                                            } finally { setBusy(null); }
                                                        }}
                                                        className="ml-1 underline text-[#3AA8A8] cursor-pointer"
                                                    >
                                                        {t('payments.release', 'release')}
                                                    </button>
                                                </p>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Scope + reason. Scope answers "is the gateway down for
                                        everyone, or is it just this hospital's account?" */}
                                    <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
                                        <select
                                            data-testid={`platform-payment-scope-${m.key}`}
                                            value={d.scope || ''}
                                            onChange={(e) => setField(m.key, 'scope', e.target.value)}
                                            className="tt-select p-2 border border-gray-300 rounded-xl text-xs cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                        >
                                            <option value="">{t('payments.scope_all', 'All hospitals')}</option>
                                            {(tenantOptions || []).map((tn) => (
                                                <option key={tn.tenantId} value={tn.tenantId}>{tn.name}</option>
                                            ))}
                                        </select>

                                        <input
                                            data-testid={`platform-payment-reason-${m.key}`}
                                            value={d.reason || ''}
                                            onChange={(e) => setField(m.key, 'reason', e.target.value)}
                                            placeholder={t('payments.reason_ph', 'Reason shown to hospitals, e.g. gateway maintenance')}
                                            className="flex-1 p-2 border border-gray-300 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                        />

                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                data-testid={`platform-payment-hold-${m.key}`}
                                                disabled={isBusy}
                                                onClick={() => apply(m.key, true)}
                                                className="px-3 py-2 rounded-xl text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50 transition cursor-pointer disabled:opacity-50"
                                            >
                                                {isBusy ? '…' : t('payments.hold', 'Hold')}
                                            </button>
                                            <button
                                                type="button"
                                                data-testid={`platform-payment-release-${m.key}`}
                                                disabled={isBusy}
                                                onClick={() => apply(m.key, false)}
                                                className="px-3 py-2 rounded-xl text-xs font-semibold border border-[#4ABDBD] text-[#3AA8A8] hover:bg-[#4ABDBD]/5 transition cursor-pointer disabled:opacity-50"
                                            >
                                                {isBusy ? '…' : t('payments.release_btn', 'Release')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Audit trail — answers "why couldn't we take online payment last Tuesday?" */}
            {holds?.history?.length > 0 && (
                <div className="rounded-2xl border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2 text-sm">
                        <History className="w-4 h-4 text-[#4ABDBD]" />
                        {t('payments.hold_history', 'Recent holds')}
                    </h3>
                    <div className="flex flex-col gap-1.5">
                        {holds.history.map((h) => (
                            <p key={h.id} className="text-[11px] text-gray-500">
                                <span className="font-semibold text-gray-700">{h.method}</span>
                                {' · '}
                                {h.tenantId
                                    ? ((tenantOptions || []).find((x) => x.tenantId === h.tenantId)?.name || t('healthRecord.details.hospital', 'Hospital'))
                                    : t('payments.scope_all', 'All hospitals')}
                                {h.reason ? ` · ${h.reason}` : ''}
                                {holdActor(h) ? ` · ${t('payments.by', 'by')} ${holdActor(h)}` : ''}
                            </p>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
